import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { BottomSheet } from '@beeline/design-system-react/extracted-styles';
import { BottomSheetActionsType } from '@beeline/design-system-react/types/components/BottomSheet/BottomSheet.types';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { useHost } from 'src/providers/HostProvider';
import { getAdminState } from 'src/store/admin/selectors';
import { adminActions } from 'src/store/admin/slice';
import { removeBuilding } from 'src/store/buildings/services';
import { useAppDispatch } from 'src/store/useAppDispatch';

import { AdministratorBuildingFields } from '../AdministratorBuildingForm/AdministratorBuildingFields';
import { AdministratorBuildingForm } from '../AdministratorBuildingForm/AdministratorBuildingForm';

import classes from './BuildingBottomSheet.module.scss';

const Fields: FC = () => {
  const id = useField<number | undefined>('id').input.value;
  const dispatch = useAppDispatch();

  const { submit } = useForm();

  const { buildingIsOpened } = useSelector(getAdminState);

  const close = useCallback(() => {
    dispatch(adminActions.closeBuilding());
  }, [dispatch]);

  const saveBuilding = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');

    close();
  }, [close, submit]);

  const deleteBuilding = useCallback(async () => {
    if (id) await dispatch(removeBuilding(id));

    close();
  }, [close, dispatch, id]);

  const actions = useMemo((): BottomSheetActionsType | undefined => {
    const result: BottomSheetActionsType = [
      {
        label: id ? 'Сохранить' : 'Создать',
        variant: 'contained',
        onClick: saveBuilding,
      },
    ];
    if (id)
      result.unshift({
        label: 'Удалить',
        variant: 'outlined',
        onClick: deleteBuilding,
      });

    return result;
  }, [deleteBuilding, id, saveBuilding]);

  const handleSetOpen = useCallback(
    (value: boolean) => {
      dispatch(adminActions.setBuildingIsOpened(value));
    },
    [dispatch],
  );

  return (
    <BottomSheet
      isOpen={buildingIsOpened}
      handleSetOpen={handleSetOpen}
      swiper
      modal={false}
      snapPoints={[1]}
      className={classes.settings}
      initialSnapPoint={0}
      header={{
        icon: Icons.Building,
        title: id ? 'Редактирование офиса' : 'Создание офиса',
        closeButton: true,
      }}
      actions={actions}
    >
      <div className={classes.container}>
        <AdministratorBuildingFields />
      </div>
    </BottomSheet>
  );
};

const FormBottomSheet: FC = () => {
  return (
    <AdministratorBuildingForm>
      <Fields />
    </AdministratorBuildingForm>
  );
};

export const BuildingBottomSheet: FC = () => {
  const { host } = useHost();

  if (!host) return null;

  return createPortal(<FormBottomSheet />, host);
};
